/*
 *  ************************************************************************
 *  *  © [2015 - 2024] Quintype Technologies India Private Limited
 *  *  All Rights Reserved.
 *  *************************************************************************
 */
import React, { useState } from 'react';
import { Headline } from '../../atoms/headline';
import { useDispatch, useSelector } from 'react-redux';
import get from 'lodash/get';
import { Button } from '../../atoms/button';
import { triggerEmailCheck, triggerSendOtp, genericError, getErrorStyle, inputFieldError, handleScrollToTop } from '../../util';
import {
  CREATE_PASSWORD,
  EMAIL_LABEL,
  ENTER_NAME,
  LOGIN_LABEL,
  QUILL_EMPTY_STRING,
  UPDATE_SIGN_IN,
  UPDATE_SIGN_UP,
  UPDATE_SINGLE_SIGN_ON,
  SIGN_UP_HEADLINE,
  GET_OTP_BUTTON,
  ALREADY_HAVE_ACCOUNT
} from '../../../../../constants';
import './sign-up.module.css';
import { ErrorMessage } from '../../atoms/error-message';
import { SvgHandler } from '../../../../svg-handler/svg-handler';
import visibilityIcon from '../../../../../../assets/icons/visibility.svg';
import visibilityOffIcon from '../../../../../../assets/icons/visibility-off.svg';
import { getPath } from '../../../../../utils';

const Subheadline = ({ email, loginLabel, pageSubTitle, isUserLoginPage }) => {
  const dispatch = useDispatch();
  const sketchesHost = useSelector(state => get(state, ['qt', 'config', 'sketches-host'], {}));
  const whiteListedUrlParams = useSelector(state => get(state, ['qt', 'config', 'pagebuilder-config', 'general', 'others', 'whiteListedUrlParams'], []));
  
  const triggerLogin = () => {
    dispatch({
      type: !isUserLoginPage ? UPDATE_SIGN_IN : UPDATE_SINGLE_SIGN_ON,
      payload: { currentScreen: 'signIn', userData: { inputData: email } }
    });
    if (!isUserLoginPage) {
      window.location.href = getPath({
        sketchesHost,
        relativePath: '/sign-in',
        injectCustomParams: true,
        whiteListedUrlParams
      });
    }
  };

  return (
    <div styleName='subheadline'>
      {pageSubTitle || ALREADY_HAVE_ACCOUNT}
      <div onClick={() => triggerLogin()} styleName='login-label' className='auth--text-color'>
        {loginLabel || LOGIN_LABEL}
      </div>
    </div>
  );
};

const InputField = ({
  onEmailChange,
  onPasswordChange,
  onNameChange,
  email,
  password,
  name,
  response,
  nameLabel,
  passwordLabel,
  emailLabel,
  mode,
  api
}) => {
  const [hidePassword, setPassword] = useState(true);
  const emailError = getErrorStyle(response, api, mode);
  return (
    <>
      <div key='email' className={emailError}>
        <label for='email'>{emailLabel || EMAIL_LABEL}</label>
        <input type='text' value={email} id='email' onChange={event => onEmailChange(event)} name='email' required />
        {inputFieldError(response, api) ? <ErrorMessage response={response} api={api} mode={mode} /> : <></>}
      </div>
      <div key='password' styleName='password-wrapper'>
        <label for='password'>{passwordLabel || CREATE_PASSWORD}</label>
        <input
          type={hidePassword ? 'password' : 'text'}
          value={password}
          id='password'
          onChange={event => onPasswordChange(event)}
          name='password'
          required
        />
        <div styleName='eye-wrapper' onClick={() => setPassword(!hidePassword)}>
          <SvgHandler
            xlinkHref={hidePassword ? visibilityIcon : visibilityOffIcon}
            width='24'
            height='24'
            viewBox='0 0 24 24'
            invertColor={mode === 'dark'}
          />
        </div>
      </div>
      <div key='name' styleName='name-wrapper'>
        <label for='name'>{nameLabel || ENTER_NAME}</label>
        <input type='text' value={name} id='name' onChange={event => onNameChange(event)} name='name' required />
      </div>
    </>
  );
};

export const SignUpScreen = ({ mode, isUserLoginPage = false }) => {
  const { signUp = {}, singleSignOn = {} } = useSelector(state => get(state, ['login'], {}));
  const { inputData = '', name: userName = '', password: userPassword = '' } = get(signUp, ['userData'], {});
  const {
    inputData: ssoInputData = '',
    name: ssoName = '',
    password: ssoPassword = ''
  } = get(singleSignOn, ['userData'], {});
  const { button, emailLabel, loginLabel, passwordLabel, nameLabel, pageSubTitle, pageTitle } = useSelector(state =>
    get(state, ['qt', 'config', 'pagebuilder-config', 'login', 'sign-up', 'rows', 0, 'config'], {})
  );
  const localStorageEmail = global?.localStorage?.getItem('userEmail');
  const emailData = inputData || localStorageEmail;
  const [email, setEmail] = useState(!isUserLoginPage ? emailData : ssoInputData);
  const [password, setPassword] = useState(!isUserLoginPage ? userPassword : ssoPassword);
  const [name, setName] = useState(!isUserLoginPage ? userName : ssoName);
  const [response, setResponse] = useState(null);
  const [api, setApi] = useState(null);
  const dispatch = useDispatch();
  const sketchesHost = useSelector(state => get(state, ['qt', 'config', 'sketches-host'], {}));

  handleScrollToTop();

  const triggerHandler = async () => {
    global?.localStorage?.setItem('userEmail', email);
    const emailCheckResponse = await triggerEmailCheck({ email, sketchesHost });

    if (!emailCheckResponse) {
      const otpResponse = await triggerSendOtp({ email, sketchesHost });
      setResponse(otpResponse);
      setApi('send-otp-email');
      if (otpResponse === 200) {
        dispatch({
          type: !isUserLoginPage ? UPDATE_SIGN_UP : UPDATE_SINGLE_SIGN_ON,
          payload: {
            currentScreen: !isUserLoginPage ? 'otp' : 'emailOtp',
            userData: { inputData: email, name: name, password: password }
          }
        });
      }
    } else {
      setResponse(200);
      setApi('email-check');
    }
  };

  return (
    <div className={`auth--wrapper ${mode}`}>
      <Headline content={pageTitle && pageTitle !== QUILL_EMPTY_STRING ? pageTitle : SIGN_UP_HEADLINE} />
      <Subheadline
        email={email}
        loginLabel={loginLabel}
        pageSubTitle={pageSubTitle}
        isUserLoginPage={isUserLoginPage}
      />
      <InputField
        onEmailChange={e => {
          setResponse(null);
          setApi(null);
          setEmail(e.target.value);
        }}
        onPasswordChange={e => {
          setResponse(null);
          setApi(null);
          setPassword(e.target.value);
        }}
        onNameChange={e => {
          setResponse(null);
          setApi(null);
          setName(e.target.value);
        }}
        email={email}
        password={password}
        name={name}
        response={response}
        emailLabel={emailLabel}
        nameLabel={nameLabel}
        passwordLabel={passwordLabel}
        mode={mode}
        api={api}
      />
      <Button
        text={button || GET_OTP_BUTTON}
        clickHandler={() => triggerHandler()}
        disable={!email || !password || !name}
        enableEnterClick={true}
      />
      {genericError(response, api) ? <ErrorMessage response={response} api={api} mode={mode} /> : <></>}
    </div>
  );
};
